/**
 * VNC Console Module
 *
 * Provides embedded VNC console using noVNC
 */

import type { VmVncInfo } from '../api/types.js';
import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { generateComboSelect } from '../ui/forms.js';
import { showRuntimePanel, runtimeHotplugDrive } from '../vm/runtime.js';
import { pasteDialog, formDialog } from '../ui/dialogs.js';
import RFBImport from '@novnc/novnc/lib/rfb.js';
import KeysymDefImport from '@novnc/novnc/lib/input/keysymdef.js';

// esbuild's CJS-to-ESM interop can double-wrap the default export when
// bundling as IIFE, leaving the constructor at .default.default instead of
// .default.  Unwrap at runtime so it works regardless.
const RFB: typeof RFBImport = typeof RFBImport === 'function'
  ? RFBImport
  : (RFBImport as unknown as { default: typeof RFBImport }).default;

interface KeysymDefModule {
  lookup(codepoint: number): number;
}
const KeysymDef: KeysymDefModule =
  typeof (KeysymDefImport as unknown as KeysymDefModule).lookup === 'function'
    ? (KeysymDefImport as unknown as KeysymDefModule)
    : ((KeysymDefImport as unknown as { default: KeysymDefModule }).default);

let rfbConnection: InstanceType<typeof RFB> | null = null;
let currentVmId: number | null = null;
let currentVmHost: string | null = null;
let swapCtrlCapsLock: boolean = localStorage.getItem('vnc_swap_ctrl_caps') === 'true';

// Reconnection state
let currentWsUrl: string | null = null;
let currentPassword: string | undefined = undefined;
let currentViewOnly: boolean = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay: number = 1000; // Start at 1 second
const MAX_RECONNECT_DELAY = 30000; // Cap at 30 seconds

// X11 keysyms for key remapping
const XK_Control_L = 0xFFE3;
const XK_Control_R = 0xFFE4;
const XK_Caps_Lock = 0xFFE5;

// XK_Return, XK_Tab, XK_BackSpace are defined further down for the paste
// path; the soft keyboard input handler reuses them via forward reference
// (TypeScript const hoisting is fine here since they're referenced inside
// function bodies, not at module init).

// Extended RFB interface for the methods we use beyond what the .d.ts
// originally defined. These match what noVNC's RFB exposes; the typings
// in src/types/novnc.d.ts have been updated to match, but several call
// sites still cast through this interface for historical reasons.
interface RFBWithSendKey {
  // sendKey(keysym, code) — sends both press AND release in one call.
  // sendKey(keysym, code, true/false) — sends one direction explicitly.
  sendKey(keysym: number, code?: string | null, down?: boolean): void;
  // When false, canvas clicks no longer auto-focus the canvas; the soft
  // keyboard helper toggles this so canvas taps don't dismiss the OS kb.
  focusOnClick: boolean;
}

// Keyboard remapping: swap Ctrl and CapsLock
function setupKeyboardRemapping(container: HTMLElement): void {
  const handleKey = (e: KeyboardEvent) => {
    if (!rfbConnection || !swapCtrlCapsLock) return;

    // Only intercept keys we want to remap
    const isCapsLock = e.code === 'CapsLock';
    const isLeftCtrl = e.code === 'ControlLeft';
    const isRightCtrl = e.code === 'ControlRight';

    if (!isCapsLock && !isLeftCtrl && !isRightCtrl) return;

    // Prevent the original key from reaching noVNC
    e.preventDefault();
    e.stopPropagation();

    const down = e.type === 'keydown';
    const rfb = rfbConnection as unknown as RFBWithSendKey;

    if (isCapsLock) {
      // CapsLock -> send Left Ctrl
      rfb.sendKey(XK_Control_L, 'ControlLeft', down);
    } else if (isLeftCtrl) {
      // Left Ctrl -> send CapsLock
      rfb.sendKey(XK_Caps_Lock, 'CapsLock', down);
    } else if (isRightCtrl) {
      // Right Ctrl stays as Ctrl (only swap left Ctrl with CapsLock)
      rfb.sendKey(XK_Control_R, 'ControlRight', down);
    }
  };

  // Use capture phase to intercept before noVNC's handlers
  container.addEventListener('keydown', handleKey, { capture: true });
  container.addEventListener('keyup', handleKey, { capture: true });
}

/**
 * Toggle Ctrl/CapsLock swap and update button state
 */
export function toggleSwapCtrlCaps(): void {
  swapCtrlCapsLock = !swapCtrlCapsLock;
  localStorage.setItem('vnc_swap_ctrl_caps', swapCtrlCapsLock ? 'true' : 'false');
  updateSwapCtrlCapsButton();
}

function updateSwapCtrlCapsButton(): void {
  const btn = document.getElementById('vnc-swap-ctrl-caps-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', swapCtrlCapsLock);
    btn.title = swapCtrlCapsLock ? 'Ctrl/CapsLock Swap: ON' : 'Ctrl/CapsLock Swap: OFF';
  }
}

// ============================================================================
// Soft Keyboard (mobile on-screen keyboard support)
// ============================================================================
//
// noVNC's keyboard event listeners are bound to the canvas, but mobile
// browsers categorically refuse to summon the OS soft keyboard for a
// <canvas>. The only way to get the keyboard up is to focus a real input
// element. We use a hidden <textarea> for this and forward its input events
// into noVNC via rfb.sendKey().
//
// THIS IMPLEMENTATION INTENTIONALLY MIRRORS noVNC's REFERENCE UI
// (app/ui.js in upstream noVNC, functions: showVirtualKeyboard, keyInput,
// keepVirtualKeyboard, onfocusVirtualKeyboard, onblurVirtualKeyboard).
// Several attempts to deviate from that pattern produced subtle bugs:
//   - opacity:0 / pointer-events:none CSS caused taps to bleed through
//     to the canvas, which stole focus and dismissed the keyboard.
//   - position:fixed at the viewport edge caused the browser to scroll
//     the page on focus.
//   - Without toggling rfb.focusOnClick, any pointer event on the canvas
//     would re-focus it and dismiss the soft keyboard.
//   - Without a document-level mousedown capture handler that prevents
//     focus changes while the textarea is active, taps anywhere outside
//     the textarea would dismiss the keyboard.
// All four of those points are fixed by mirroring the reference. The CSS
// for #vnc-soft-kb in vmsman.css matches noVNC's #noVNC_keyboardinput.

const SOFT_KB_DEFAULT_LEN = 5;        // matches noVNC's defaultKeyboardinputLen
let softKbLastValue: string | null = null;

function getSoftKbTextarea(): HTMLTextAreaElement | null {
  return document.getElementById('vnc-soft-kb') as HTMLTextAreaElement | null;
}

// Reset the textarea to a string of N underscores. The padding gives
// backspace something to "delete" so the input-event diff sees a length
// decrease, even on browsers that don't fire keydown for backspace.
function resetSoftKbTextarea(): void {
  const ta = getSoftKbTextarea();
  if (!ta) return;
  ta.value = new Array(SOFT_KB_DEFAULT_LEN).join('_');
  softKbLastValue = ta.value;
}

// Diff the textarea content against the previous value to figure out what
// the user typed. This handles soft keyboards that don't fire keydown
// reliably (Android stock keyboard, iOS Safari, etc.).
//
// The diff logic is taken almost verbatim from noVNC's UI.keyInput so we
// inherit its battle-tested handling of: text autocorrect (chars in the
// middle changing), trailing whitespace not counted by some browsers,
// and the empty-textarea blur+focus dance for Android keyboard recovery.
function handleSoftKbInput(event: Event): void {
  if (!rfbConnection) return;
  const ta = event.target as HTMLTextAreaElement;
  const newValue = ta.value;

  if (softKbLastValue == null) {
    resetSoftKbTextarea();
  }
  const oldValue = softKbLastValue ?? '';

  // Some browsers don't count trailing whitespace in value.length; use
  // selectionStart as a more reliable length when available.
  let newLen: number;
  try {
    newLen = Math.max(ta.selectionStart, newValue.length);
  } catch {
    newLen = newValue.length;
  }
  const oldLen = oldValue.length;

  let inputs = newLen - oldLen;
  let backspaces = inputs < 0 ? -inputs : 0;

  // Compare the old string with the new char-by-char to detect text
  // autocorrect or other in-place modifications. If a difference is found
  // before the end, treat everything from that point onward as
  // "backspace the old chars then type the new chars".
  for (let i = 0; i < Math.min(oldLen, newLen); i++) {
    if (newValue.charAt(i) !== oldValue.charAt(i)) {
      inputs = newLen - i;
      backspaces = oldLen - i;
      break;
    }
  }

  const rfb = rfbConnection as unknown as RFBWithSendKey;

  // Send backspaces for any chars removed from the buffer
  for (let i = 0; i < backspaces; i++) {
    rfb.sendKey(XK_BackSpace, 'Backspace');
  }
  // Send keysyms for any new chars
  for (let i = newLen - inputs; i < newLen; i++) {
    const cp = newValue.charCodeAt(i);
    // '\n' from soft-keyboard Enter → translate to XK_Return
    if (cp === 10 || cp === 13) {
      rfb.sendKey(XK_Return, 'Enter');
      continue;
    }
    const keysym = KeysymDef.lookup(cp);
    if (keysym) rfb.sendKey(keysym);
  }

  // Manage the textarea content length so it doesn't grow without bound
  // and so the padding stays present for backspace.
  if (newLen > 2 * SOFT_KB_DEFAULT_LEN) {
    resetSoftKbTextarea();
  } else if (newLen < 1) {
    // Android keyboard quirk: if the textarea becomes empty, the OS
    // sometimes thinks the input ended. Reset to padding, then do a
    // blur+refocus dance (out-of-handler via setTimeout) so Android
    // re-recognizes the field as active. This momentary blur is the
    // documented noVNC workaround.
    resetSoftKbTextarea();
    ta.blur();
    setTimeout(() => ta.focus({ preventScroll: true }), 0);
  } else {
    softKbLastValue = newValue;
  }
}

// Document-level mousedown handler in capture phase. Prevents any pointer
// activity outside the textarea from moving focus while the soft keyboard
// is up — without this, a stray tap on the canvas re-focuses the canvas
// and the OS dismisses the keyboard.
function keepSoftKeyboardFocus(event: MouseEvent): void {
  const ta = getSoftKbTextarea();
  if (!ta || document.activeElement !== ta) return;

  // Allow focus to move to other real form fields if the user
  // intentionally taps one (e.g., a dialog input).
  const target = event.target as HTMLElement | null;
  if (target && (target as HTMLInputElement).form !== undefined) {
    const t = (target as HTMLInputElement).type;
    if (t === 'text' || t === 'email' || t === 'search' || t === 'password' ||
        t === 'tel' || t === 'url' || t === 'textarea' ||
        t === 'select-one' || t === 'select-multiple') {
      return;
    }
  }
  event.preventDefault();
}

function onSoftKbFocus(): void {
  // Tell noVNC's RFB to stop auto-focusing the canvas on click; otherwise
  // any canvas pointer event would steal focus from the textarea and
  // dismiss the soft keyboard.
  if (rfbConnection) {
    (rfbConnection as unknown as RFBWithSendKey).focusOnClick = false;
  }
  updateKeyboardButton();
}

function onSoftKbBlur(): void {
  if (rfbConnection) {
    (rfbConnection as unknown as RFBWithSendKey).focusOnClick = true;
  }
  // setTimeout so updateKeyboardButton sees the post-blur activeElement.
  setTimeout(updateKeyboardButton, 0);
}

let softKbWired = false;
function setupSoftKeyboard(): void {
  const ta = getSoftKbTextarea();
  if (!ta) return;
  resetSoftKbTextarea();
  ta.addEventListener('input', handleSoftKbInput);
  ta.addEventListener('focus', onSoftKbFocus);
  ta.addEventListener('blur', onSoftKbBlur);
  // Suppress form submission on Enter (noVNC reference does the same)
  ta.addEventListener('submit', (e) => e.preventDefault());

  // Install the global focus-keeper exactly once across the lifetime of
  // the page. Multiple console dialogs share it; the handler is a no-op
  // unless the textarea is currently focused.
  if (!softKbWired) {
    document.documentElement.addEventListener(
      'mousedown', keepSoftKeyboardFocus, true);
    softKbWired = true;
  }
}

/**
 * Toggle the mobile on-screen keyboard.
 *
 * Focusing the hidden textarea summons the OS soft keyboard on iOS/Android;
 * blurring it dismisses the keyboard. Mirrors noVNC reference UI's
 * showVirtualKeyboard / hideVirtualKeyboard.
 */
export function toggleKeyboard(): void {
  if (!rfbConnection) return;
  const ta = getSoftKbTextarea();
  if (!ta) return;

  if (document.activeElement === ta) {
    ta.blur();
  } else {
    ta.focus({ preventScroll: true });
    try {
      const l = ta.value.length;
      ta.setSelectionRange(l, l);
    } catch {
      // setSelectionRange undefined in some browsers — ignore
    }
  }

  setTimeout(updateKeyboardButton, 0);
}

function updateKeyboardButton(): void {
  const ta = getSoftKbTextarea();
  const active = ta != null && document.activeElement === ta;
  const btn = document.getElementById('vnc-keyboard-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', active);
  }
}

/**
 * Open a VNC console dialog for a VM
 *
 * @param vmId - VM ID
 * @param vmName - VM name for dialog title
 * @param vncInfo - VNC connection info
 * @param host - Target host where the VM runs (for delegated VMs)
 * @param wsPath - WebSocket path override (e.g. /ws/vm/:id/guest-vnc)
 * @param isGuest - If true, hide QEMU-specific toolbar buttons and adjust title
 */
export function openVncConsole(
  vmId: number, vmName: string, vncInfo: VmVncInfo,
  host?: string, wsPath?: string, isGuest?: boolean
): void {
  const dialogId = 'vnc-console-dialog';

  // Store VM ID and host for CD-ROM picker, hotplug, etc.
  currentVmId = vmId;
  currentVmHost = host ?? null;
  currentViewOnly = isGuest === true;

  // Remove existing dialog if any
  $(`#${dialogId}`).remove();

  // Always connect through the vmsmand WebSocket proxy
  const wsUrl = api.buildWsUrl(wsPath ?? `/ws/vm/${vmId}/vnc`);

  const swapBtnClass = swapCtrlCapsLock ? 'vnc-btn vnc-btn-active' : 'vnc-btn';
  const swapBtnTitle = swapCtrlCapsLock ? 'Ctrl/CapsLock Swap: ON' : 'Ctrl/CapsLock Swap: OFF';

  // Hide QEMU-specific buttons for guest VNC mode
  const qemuBtnStyle = isGuest ? ' style="display:none"' : '';
  // In guest mode, input buttons start hidden until "Allow Control" is toggled
  const inputInitStyle = isGuest ? ' style="display:none"' : '';

  const html = `
    <div id="${dialogId}">
      <div class="vnc-toolbar">
        ${isGuest ? `<button class="ui-button vnc-btn" id="vnc-allow-control-btn" onclick="vncToggleAllowControl()" title="Allow keyboard and mouse input">Allow Control</button>` : ''}
        <button class="ui-button vnc-btn vnc-input-btn" onclick="vncSendCtrlAltDel()" title="Send Ctrl+Alt+Del"${inputInitStyle}>
          Ctrl+Alt+Del
        </button>
        <button class="ui-button vnc-btn" onclick="vncToggleFullscreen()" title="Toggle Fullscreen">
          Fullscreen
        </button>
        <button class="ui-button vnc-btn" onclick="vncShowCdromPicker()" title="Mount/Unmount CD-ROM"${qemuBtnStyle}>
          CD
        </button>
        <button class="ui-button vnc-btn" onclick="vncHotplugDrive()" title="Hotplug a Drive"${qemuBtnStyle}>
          Drive
        </button>
        <button class="ui-button vnc-btn" onclick="vncShowDevices()" title="USB and Drive Hotplug"${qemuBtnStyle}>
          Devices
        </button>
        <button class="ui-button ${swapBtnClass} vnc-input-btn" id="vnc-swap-ctrl-caps-btn" onclick="vncToggleSwapCtrlCaps()" title="${swapBtnTitle}"${inputInitStyle}>
          Ctrl/Caps
        </button>
        <button class="ui-button vnc-btn vnc-input-btn" onclick="vncPaste()" title="Paste clipboard as keystrokes"${inputInitStyle}>
          Paste
        </button>
        <button class="ui-button vnc-btn mobile-kbd-btn vnc-input-btn" id="vnc-keyboard-btn" onclick="vncToggleKeyboard()" title="Toggle On-Screen Keyboard"${inputInitStyle}>
          Keyboard
        </button>
        <span class="vnc-status" id="vnc-status">Connecting...</span>
      </div>
      <div id="vnc-screen" class="vnc-screen"></div>
      <textarea id="vnc-soft-kb" class="vnc-soft-kb"
                autocapitalize="off" autocomplete="off"
                autocorrect="off" spellcheck="false"
                tabindex="-1" aria-hidden="true"></textarea>
    </div>
  `;

  const titlePrefix = isGuest ? 'Screen View' : 'Console';

  $(html).appendTo('body').dialog({
    modal: true,
    title: `${titlePrefix} - ${vmName}`,
    width: Math.min(1280, $(window).width()! - 100),
    height: Math.min(800, $(window).height()! - 100),
    resizable: true,
    close: function () {
      disconnectVnc();
      $(this).remove();
    },
    open: function () {
      initVncConnection(wsUrl, vncInfo.password);
      setupSoftKeyboard();
    },
  });
}

/**
 * Cancel any pending reconnection attempt
 */
function cancelReconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

/**
 * Schedule a reconnection attempt with exponential backoff
 */
function scheduleReconnect(): void {
  if (!currentWsUrl) return;

  const statusEl = document.getElementById('vnc-status');
  if (statusEl) {
    const delaySec = Math.round(reconnectDelay / 1000);
    statusEl.textContent = `Reconnecting in ${delaySec}s...`;
    statusEl.style.color = '#c90';
  }

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (currentWsUrl) {
      const statusEl = document.getElementById('vnc-status');
      if (statusEl) {
        statusEl.textContent = 'Reconnecting...';
        statusEl.style.color = '#c90';
      }
      initVncConnection(currentWsUrl, currentPassword);
    }
  }, reconnectDelay);

  // Exponential backoff: double the delay for next attempt, cap at max
  reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
}

/**
 * Initialize VNC connection
 */
function initVncConnection(wsUrl: string, password?: string): void {
  const container = document.getElementById('vnc-screen');
  const statusEl = document.getElementById('vnc-status');

  if (!container || !statusEl) return;

  // Store connection params for reconnection
  currentWsUrl = wsUrl;
  currentPassword = password;

  // Clear any existing content in the container (from previous connection)
  // noVNC creates its own canvas/elements
  if (rfbConnection) {
    try {
      rfbConnection.disconnect();
    } catch {
      // Ignore errors during cleanup
    }
    rfbConnection = null;
  }

  try {
    // Create RFB connection
    const options: { credentials?: { password?: string } } = {};
    if (password) {
      options.credentials = { password };
    }

    rfbConnection = new RFB(container, wsUrl, options);
    rfbConnection.scaleViewport = true;
    rfbConnection.resizeSession = true;
    rfbConnection.viewOnly = currentViewOnly;

    // Set up Ctrl/CapsLock swap
    setupKeyboardRemapping(container);

    // When mouse enters the VNC screen, focus the canvas to capture keyboard input.
    // This prevents toolbar buttons from stealing Enter/Space key presses after being clicked.
    // Use a one-time setup flag to avoid adding duplicate listeners on reconnect.
    if (!(container as HTMLElement & { _vncMouseEnterSetup?: boolean })._vncMouseEnterSetup) {
      (container as HTMLElement & { _vncMouseEnterSetup?: boolean })._vncMouseEnterSetup = true;
      container.addEventListener('mouseenter', () => {
        // Blur any focused toolbar button
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        // Focus the canvas
        const canvas = container.querySelector('canvas');
        canvas?.focus();
      });
    }

    // Event handlers
    rfbConnection.addEventListener('connect', () => {
      // Reset backoff on successful connection
      reconnectDelay = 1000;
      cancelReconnect();

      statusEl.textContent = 'Connected';
      statusEl.style.color = '#0a0';
      // Focus the VNC canvas so keyboard input goes to the VM, not the toolbar buttons
      const canvas = container.querySelector('canvas');
      canvas?.focus();

      // Sync mobile keyboard button state when focus is lost externally
      const textarea = container.querySelector('textarea');
      textarea?.addEventListener('blur', updateKeyboardButton);
    });

    rfbConnection.addEventListener('disconnect', (e: CustomEvent) => {
      const detail = e.detail as { clean?: boolean };
      rfbConnection = null;

      if (detail.clean) {
        // Clean disconnect (user closed, or VM stopped) - don't reconnect
        statusEl.textContent = 'Disconnected';
        statusEl.style.color = '#666';
      } else {
        // Unclean disconnect - attempt reconnection
        statusEl.textContent = 'Connection lost';
        statusEl.style.color = '#c00';
        scheduleReconnect();
      }
    });

    rfbConnection.addEventListener('desktopname', (e: CustomEvent) => {
      const detail = e.detail as { name?: string };
      const dialog = $('#vnc-console-dialog').closest('.ui-dialog');
      if (dialog.length && detail.name) {
        dialog.find('.ui-dialog-title').text(`Console - ${detail.name}`);
      }
    });

    rfbConnection.addEventListener('credentialsrequired', () => {
      const rfb = rfbConnection;
      if (!rfb) return;

      statusEl.textContent = 'Password required';
      statusEl.style.color = '#c90';

      formDialog(
        'VNC Password',
        '<label for="vnc-cred-password">Password:</label>' +
        '<input type="password" id="vnc-cred-password" style="width:100%;margin-top:4px">',
        () => {
          const pw = ($('#vnc-cred-password').val() as string) || '';
          rfb.sendCredentials({ password: pw });
        },
        {
          submitLabel: 'Connect',
          width: 300,
          onOpen: () => { $('#vnc-cred-password').trigger('focus'); },
        }
      );
    });

  } catch (err) {
    console.error('Failed to create VNC connection:', err);
    statusEl.textContent = `Failed: ${err instanceof Error ? err.message : 'Unknown error'}`;
    statusEl.style.color = '#c00';
    // Schedule reconnect on connection failure too
    scheduleReconnect();
  }
}

/**
 * Disconnect VNC and stop any reconnection attempts
 */
export function disconnectVnc(): void {
  // Cancel any pending reconnection
  cancelReconnect();

  // Clear connection state to prevent reconnection
  currentWsUrl = null;
  currentPassword = undefined;
  reconnectDelay = 1000; // Reset backoff

  if (rfbConnection) {
    rfbConnection.disconnect();
    rfbConnection = null;
  }
}

/**
 * Send Ctrl+Alt+Del
 */
export function sendCtrlAltDel(): void {
  if (rfbConnection) {
    rfbConnection.sendCtrlAltDel();
  }
}

/**
 * Toggle view-only mode for guest VNC (Allow Control button).
 * Shows/hides input-related toolbar buttons and flips rfb.viewOnly.
 */
export function toggleAllowControl(): void {
  currentViewOnly = !currentViewOnly;
  if (rfbConnection) {
    rfbConnection.viewOnly = currentViewOnly;
  }
  const btn = document.getElementById('vnc-allow-control-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', !currentViewOnly);
    btn.title = currentViewOnly ? 'Allow keyboard and mouse input' : 'Input enabled — click to disable';
  }
  // Show/hide input-related buttons
  document.querySelectorAll('.vnc-input-btn').forEach(el => {
    (el as HTMLElement).style.display = currentViewOnly ? 'none' : '';
  });
}

/**
 * Paste clipboard text by sending individual key events via VNC.
 * Characters are sent with a small delay between them to avoid
 * overwhelming the guest's keyboard input buffer.
 */
const VNC_PASTE_DELAY_MS = 10;

// X11 keysym constants
const XK_Return    = 0xFF0D;
const XK_Tab       = 0xFF09;
const XK_BackSpace = 0xFF08;
const XK_Shift_L   = 0xFFE1;

// Map of shifted characters to their base keysym (US keyboard layout)
// When we encounter a shifted char, we send Shift down, base key, Shift up
const SHIFTED_CHAR_MAP: Record<string, number> = {
  // Uppercase letters -> lowercase keysyms
  ...((): Record<string, number> => {
    const map: Record<string, number> = {};
    for (let i = 65; i <= 90; i++) { // A-Z
      map[String.fromCharCode(i)] = i + 32; // a-z
    }
    return map;
  })(),
  // Number row symbols -> base number keysyms
  '!': 0x31, // 1
  '@': 0x32, // 2
  '#': 0x33, // 3
  '$': 0x34, // 4
  '%': 0x35, // 5
  '^': 0x36, // 6
  '&': 0x37, // 7
  '*': 0x38, // 8
  '(': 0x39, // 9
  ')': 0x30, // 0
  // Other shifted symbols
  '~': 0x60, // `
  '_': 0x2d, // -
  '+': 0x3d, // =
  '{': 0x5b, // [
  '}': 0x5d, // ]
  '|': 0x5c, // \
  ':': 0x3b, // ;
  '"': 0x27, // '
  '<': 0x2c, // ,
  '>': 0x2e, // .
  '?': 0x2f, // /
};

interface KeyAction {
  keysym: number;
  shifted: boolean;
}

function charToKeyAction(char: string): KeyAction | null {
  const cp = char.codePointAt(0);
  if (cp === undefined) return null;

  if (cp === 0x0A || cp === 0x0D) return { keysym: XK_Return, shifted: false };
  if (cp === 0x09) return { keysym: XK_Tab, shifted: false };
  if (cp === 0x08) return { keysym: XK_BackSpace, shifted: false };

  // Check if this is a shifted character
  if (char in SHIFTED_CHAR_MAP) {
    return { keysym: SHIFTED_CHAR_MAP[char], shifted: true };
  }

  // Latin-1 range (space through U+00FF): keysym = code point
  if (cp >= 0x20 && cp <= 0x00FF) return { keysym: cp, shifted: false };

  // Unicode above Latin-1: X11 Unicode keysym convention
  if (cp >= 0x0100) return { keysym: 0x01000000 | cp, shifted: false };

  return null;  // Unprintable control character
}

export function pasteClipboard(): void {
  if (!rfbConnection) return;

  pasteDialog((text) => {
    if (!rfbConnection) return;

    const rfb = rfbConnection as unknown as RFBWithSendKey;
    const chars = Array.from(text);
    let i = 0;

    function sendNext(): void {
      if (!rfbConnection || i >= chars.length) return;

      // Skip \r in \r\n pairs — the \n will send Return
      if (chars[i] === '\r' && i + 1 < chars.length && chars[i + 1] === '\n') {
        i++;
        sendNext();
        return;
      }

      const action = charToKeyAction(chars[i]);
      if (action !== null) {
        if (action.shifted) {
          // Send: Shift down, key press, Shift up
          rfb.sendKey(XK_Shift_L, 'ShiftLeft', true);
          rfb.sendKey(action.keysym, null);
          rfb.sendKey(XK_Shift_L, 'ShiftLeft', false);
        } else {
          rfb.sendKey(action.keysym, null);
        }
      }
      i++;
      if (i < chars.length) {
        setTimeout(sendNext, VNC_PASTE_DELAY_MS);
      }
    }

    sendNext();
  });
}

/**
 * Toggle fullscreen mode.
 *
 * Fullscreens the entire dialog (toolbar + screen + soft-kb textarea), not
 * just the screen element, so the toolbar buttons (Keyboard, Ctrl+Alt+Del,
 * etc.) remain reachable while in fullscreen. CSS rules in vmsman.css
 * (#vnc-console-dialog:fullscreen) handle the layout so the canvas still
 * fills the available area below the toolbar.
 */
export function toggleFullscreen(): void {
  const dialog = document.getElementById('vnc-console-dialog');
  if (!dialog) return;

  if (!document.fullscreenElement) {
    dialog.requestFullscreen?.();
  } else {
    document.exitFullscreen?.();
  }
}

/**
 * Show CD-ROM ISO picker dialog
 */
export async function showCdromPicker(): Promise<void> {
  if (currentVmId === null) return;
  const vmId = currentVmId;

  const isoData = await apiCall(api.getIsoImages(currentVmHost ?? undefined), 'Failed to Load ISO Images');
  if (!isoData) return;

  const content = '<div><h4>Select ISO Image</h4>'
    + generateComboSelect('cdrom_iso', 'none', isoData)
    + '</div>';

  $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: 'Mount CD-ROM',
      zIndex: 10001,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Mount: async function () {
          const iso = $('#cdrom_iso').val() as string;
          if (!iso || iso === 'none') return;
          const dlg = $(this);
          dlg.dialog('disable');
          const result = await apiCall(api.mountCdrom(vmId, iso), 'Failed to Mount CD-ROM');
          if (result !== null) {
            dlg.dialog('close');
          } else {
            dlg.dialog('enable');
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Hotplug a drive to the current VM
 */
export function hotplugDrive(): void {
  if (currentVmId === null) return;
  runtimeHotplugDrive(currentVmId, currentVmHost ?? undefined);
}

/**
 * Open the USB/Drive hotplug panel for the current VM
 */
export function showDevices(): void {
  if (currentVmId === null) return;
  showRuntimePanel(currentVmId, currentVmHost ?? undefined);
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vncSendCtrlAltDel = sendCtrlAltDel;
  (window as unknown as Record<string, unknown>).vncToggleAllowControl = toggleAllowControl;
  (window as unknown as Record<string, unknown>).vncToggleFullscreen = toggleFullscreen;
  (window as unknown as Record<string, unknown>).vncShowCdromPicker = showCdromPicker;
  (window as unknown as Record<string, unknown>).vncHotplugDrive = hotplugDrive;
  (window as unknown as Record<string, unknown>).vncShowDevices = showDevices;
  (window as unknown as Record<string, unknown>).vncToggleSwapCtrlCaps = toggleSwapCtrlCaps;
  (window as unknown as Record<string, unknown>).vncPaste = pasteClipboard;
  (window as unknown as Record<string, unknown>).vncToggleKeyboard = toggleKeyboard;
  (window as unknown as Record<string, unknown>).disconnectVnc = disconnectVnc;
}
